import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../lib/auth";
import { API, LOGO_URL } from "../lib/constants";
import {
  LogOut, Loader2, Download, Search, Users, Sparkles,
  Mail, Phone, Building2, TrendingUp, Calendar, RefreshCw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { logout, authHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads"); // 'leads' | 'quiz'
  const [adminUser, setAdminUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [me, s, l, qz] = await Promise.all([
        axios.get(`${API}/admin/me`, { headers: authHeaders }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/admin/leads`, { headers: authHeaders }),
        axios.get(`${API}/admin/quiz-submissions`, { headers: authHeaders }),
      ]);
      setAdminUser(me.data);
      setStats(s.data);
      setLeads(l.data.leads || []);
      setQuiz(qz.data.submissions || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll();
  }, [token]);

  const filteredLeads = useMemo(() => {
    if (!q) return leads;
    const needle = q.toLowerCase();
    return leads.filter((l) =>
      [l.name, l.company, l.email, l.phone, l.designation, l.primary_challenge]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [leads, q]);

  const filteredQuiz = useMemo(() => {
    if (!q) return quiz;
    const needle = q.toLowerCase();
    return quiz.filter((s) =>
      [s.answers?.name, s.answers?.email, s.answers?.company, s.result?.score_label]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [quiz, q]);

  const downloadCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/admin/export/${kind}.csv`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `medstry-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Medstry" className="h-8 w-auto object-contain" />
            <span className="hidden md:inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700 border-l border-slate-200 pl-4">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-xs text-slate-500">{adminUser?.email}</span>
            <button
              onClick={fetchAll}
              data-testid="admin-refresh"
              className="h-9 w-9 inline-flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 mb-1">
          Dashboard
        </h1>
        <p className="text-slate-500 text-sm mb-10">
          Live view of all inbound leads and quiz submissions.
        </p>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 mb-10 border border-slate-200" data-testid="admin-stats">
          <StatTile
            icon={Users}
            label="Total Leads"
            value={stats?.total_leads ?? "—"}
            delta={stats?.leads_last_7_days != null ? `+${stats.leads_last_7_days} this week` : ""}
          />
          <StatTile
            icon={Sparkles}
            label="Quiz Submissions"
            value={stats?.total_quiz_submissions ?? "—"}
            delta={stats?.quiz_last_7_days != null ? `+${stats.quiz_last_7_days} this week` : ""}
          />
          <StatTile
            icon={TrendingUp}
            label="Conversion (Quiz → Lead)"
            value={
              stats?.total_quiz_submissions
                ? `${Math.round(((stats.total_leads || 0) / stats.total_quiz_submissions) * 100)}%`
                : "—"
            }
            delta=""
          />
          <StatTile
            icon={Calendar}
            label="This Week (Total)"
            value={(stats?.leads_last_7_days || 0) + (stats?.quiz_last_7_days || 0)}
            delta=""
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="inline-flex bg-white border border-slate-200" data-testid="admin-tabs">
            <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="tab-leads">
              Leads <span className="ml-2 text-xs text-slate-500">({leads.length})</span>
            </TabBtn>
            <TabBtn active={tab === "quiz"} onClick={() => setTab("quiz")} testid="tab-quiz">
              Quiz <span className="ml-2 text-xs text-slate-500">({quiz.length})</span>
            </TabBtn>
          </div>

          <div className="flex items-center gap-3 flex-1 md:flex-none md:min-w-[340px]">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, company, email…"
                className="h-10 pl-9 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
              />
            </div>
            <button
              onClick={() => downloadCsv(tab === "leads" ? "leads" : "quiz")}
              data-testid="admin-export"
              className="inline-flex items-center gap-2 btn-ghost px-4 h-10 text-sm font-semibold whitespace-nowrap"
            >
              <Download className="h-4 w-4" /> CSV
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white border border-slate-200 py-24 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : tab === "leads" ? (
          <LeadsTable leads={filteredLeads} onOpen={setSelected} />
        ) : (
          <QuizTable submissions={filteredQuiz} onOpen={setSelected} />
        )}
      </div>

      {selected && (
        <DetailDrawer item={selected} kind={tab} onClose={() => setSelected(null)} />
      )}
    </main>
  );
}

function StatTile({ icon: Icon, label, value, delta }) {
  return (
    <div className="bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <Icon className="h-5 w-5 text-teal-600" strokeWidth={1.5} />
      </div>
      <div className="font-display text-3xl font-extrabold tnum text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mt-2">
        {label}
      </div>
      {delta && <div className="text-xs text-teal-700 mt-2 font-medium">{delta}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2.5 text-sm font-semibold transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function LeadsTable({ leads, onOpen }) {
  if (leads.length === 0) {
    return (
      <div className="bg-white border border-slate-200 py-20 text-center text-slate-500" data-testid="leads-empty">
        No leads yet.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500">
              <Th>Date</Th>
              <Th>Name</Th>
              <Th>Company</Th>
              <Th className="hidden md:table-cell">Type</Th>
              <Th className="hidden lg:table-cell">Email</Th>
              <Th className="hidden lg:table-cell">Phone</Th>
              <Th>Challenge</Th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr
                key={l.id}
                onClick={() => onOpen({ ...l, _kind: "lead" })}
                data-testid={`lead-row-${l.id}`}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
              >
                <Td className="text-slate-500 whitespace-nowrap">{formatDate(l.created_at)}</Td>
                <Td className="font-semibold text-slate-900">{l.name}</Td>
                <Td>
                  <div className="text-slate-900">{l.company}</div>
                  {l.designation && <div className="text-xs text-slate-500">{l.designation}</div>}
                </Td>
                <Td className="hidden md:table-cell text-slate-600">{l.company_type || "—"}</Td>
                <Td className="hidden lg:table-cell text-slate-600">{l.email}</Td>
                <Td className="hidden lg:table-cell text-slate-600">{l.phone}</Td>
                <Td className="text-slate-600 max-w-xs truncate">{l.primary_challenge || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QuizTable({ submissions, onOpen }) {
  if (submissions.length === 0) {
    return (
      <div className="bg-white border border-slate-200 py-20 text-center text-slate-500" data-testid="quiz-empty">
        No quiz submissions yet.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="quiz-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500">
              <Th>Date</Th>
              <Th>Name / Company</Th>
              <Th>Score</Th>
              <Th>Revenue Opportunity</Th>
              <Th>OPEX Savings</Th>
              <Th className="hidden md:table-cell">Challenge</Th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((s) => (
              <tr
                key={s.id}
                onClick={() => onOpen({ ...s, _kind: "quiz" })}
                data-testid={`quiz-row-${s.id}`}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
              >
                <Td className="text-slate-500 whitespace-nowrap">{formatDate(s.created_at)}</Td>
                <Td>
                  <div className="font-semibold text-slate-900">
                    {s.answers?.name || "Anonymous"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {s.answers?.company || s.answers?.email || "—"}
                  </div>
                </Td>
                <Td>
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] bg-teal-50 text-teal-700 border border-teal-200">
                    {s.result?.score_label || "—"}
                  </span>
                </Td>
                <Td className="font-mono text-slate-900 tnum">
                  ₹{s.result?.opportunity_low}–{s.result?.opportunity_high} Cr
                </Td>
                <Td className="font-mono text-slate-900 tnum">
                  ₹{s.result?.savings_low}–{s.result?.savings_high} Cr
                </Td>
                <Td className="hidden md:table-cell text-slate-600 max-w-xs truncate">
                  {s.answers?.challenge || "—"}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-5 py-3.5 font-bold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 align-top ${className}`}>{children}</td>;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function DetailDrawer({ item, kind, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
      data-testid="detail-drawer"
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full md:w-[520px] bg-white h-full overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
          <div className="font-display text-lg font-bold text-slate-900">
            {kind === "leads" ? "Lead Detail" : "Quiz Detail"}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-5 text-sm">
          {kind === "leads" ? (
            <>
              <DetailRow icon={Users} label="Name" value={item.name} />
              <DetailRow icon={Building2} label="Company" value={`${item.company}${item.designation ? ` · ${item.designation}` : ""}`} />
              <DetailRow label="Company Type" value={item.company_type || "—"} />
              <DetailRow icon={Mail} label="Email" value={<a href={`mailto:${item.email}`} className="text-indigo-600 hover:underline">{item.email}</a>} />
              <DetailRow icon={Phone} label="Phone" value={<a href={`tel:${item.phone}`} className="text-indigo-600 hover:underline">{item.phone}</a>} />
              <DetailRow label="Primary Challenge" value={item.primary_challenge || "—"} />
              <DetailRow label="Message" value={item.message || "—"} />
              <DetailRow icon={Calendar} label="Submitted" value={formatDate(item.created_at)} />
              <DetailRow label="Source" value={item.source || "—"} />
            </>
          ) : (
            <>
              <DetailRow icon={Users} label="Name" value={item.answers?.name || "Anonymous"} />
              <DetailRow icon={Building2} label="Company" value={item.answers?.company || "—"} />
              <DetailRow icon={Mail} label="Email" value={item.answers?.email || "—"} />
              <div className="border-t border-slate-200 pt-5 mt-5">
                <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-3">
                  Result
                </div>
                <div className="bg-slate-50 border border-slate-200 p-5 space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Score</div>
                    <div className="font-semibold text-slate-900">{item.result?.score_label}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Revenue Opportunity</div>
                    <div className="font-display text-xl font-extrabold text-indigo-600 tnum">
                      ₹{item.result?.opportunity_low}–{item.result?.opportunity_high} Cr
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">OPEX Savings</div>
                    <div className="font-display text-xl font-extrabold text-teal-700 tnum">
                      ₹{item.result?.savings_low}–{item.result?.savings_high} Cr
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-5">
                <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-3">
                  Answers
                </div>
                <DetailRow label="Mature brands" value={item.answers?.mature_brand_count} />
                <DetailRow label="% Revenue from mature" value={item.answers?.revenue_pct} />
                <DetailRow label="Challenge" value={item.answers?.challenge} />
                <DetailRow label="Active rural coverage" value={item.answers?.rural_coverage} />
                <DetailRow label="Revenue bucket" value={`₹${item.answers?.revenue_bucket} Cr`} />
              </div>
              <DetailRow icon={Calendar} label="Submitted" value={formatDate(item.created_at)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-4">
      <div className="w-32 shrink-0">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-slate-500 font-bold">
          {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />}
          {label}
        </div>
      </div>
      <div className="flex-1 text-slate-800 break-words">{value || "—"}</div>
    </div>
  );
}
